// PanelStateContext.tsx
import React, { createContext, useContext, useReducer, useEffect, Component } from 'react';

import { type WithTranslation, withTranslation } from 'react-i18next';

import { type PanelStore, type PanelId, type PanelState } from './PanelState';

type Action =
  | { type: 'SET'; id: PanelId; payload: Partial<PanelState> }
  | { type: 'RESET'; id: PanelId };

function reducer(state: PanelStore, action: Action): PanelStore {
  switch (action.type) {
    case 'SET': {
      return {
        ...state,
        [action.id]: { ...state[action.id], ...action.payload },
      };
    }
    case 'RESET': {
      const { [action.id]: _, ...rest } = state;
      return rest;
    }
    default: {
      return state;
    }
  }
}

const Context = createContext<{
  store: PanelStore;
  dispatch: React.Dispatch<Action>;
}>({
  store: {},
  dispatch: (action: Action): void => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('PanelStateContext dispatch called outside provider', action);
    }
  },
});

export const PanelStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [store, dispatch] = useReducer(reducer, {}, () => {
    try {
      const saved = localStorage.getItem('__sailtrack_panels');
      return saved ? (JSON.parse(saved) as PanelStore) : {};
    } catch {
      return {};
    }
  });

  /* Sync → localStorage */
  useEffect(() => {
    localStorage.setItem('__sailtrack_panels', JSON.stringify(store));
  }, [store]);

  return <Context.Provider value={{ store, dispatch }}>{children}</Context.Provider>;
};

/* Hook de uso */
export function usePanelState(
  id: PanelId,
): [PanelState | undefined, (p: Partial<PanelState>) => void] {
  const { store, dispatch } = useContext(Context);
  const set = (payload: Partial<PanelState>) => dispatch({ type: 'SET', id, payload });
  return [store[id], set];
}

type MapErrorBoundaryProperties = WithTranslation & { children: React.ReactNode };

class MapErrorBoundaryBase extends Component<MapErrorBoundaryProperties, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return <div>{this.props.t('errors.mapLoad', '⚠️ Unable to load the map.')}</div>;
    }
    return this.props.children;
  }
}

export const MapErrorBoundary = withTranslation('common')(MapErrorBoundaryBase);

interface Properties {
  children: React.ReactNode;
}
interface State {
  hasError: boolean;
}

export class LayoutErrorBoundary extends Component<Properties, State> {
  state: State = { hasError: false };

  // Sólo marcamos el error si coincide con nuestro mensaje
  static getDerivedStateFromError(error: Error): State | null {
    if (error.message.includes('t.layout.get')) {
      return { hasError: true };
    }
    return null; // re-lanzará cualquier otro error
  }

  componentDidCatch(error: Error, _info: React.ErrorInfo) {
    // Opcional: log al servidor, etc.
    console.warn('Se silenció error de layout:', error);
  }

  render() {
    if (this.state.hasError) {
      // fallback: nada (o un <div/> muy discreto)
      return null;
    }
    return this.props.children;
  }
}
